// Favorites.tsx
import React, { useState, useEffect } from 'react';
import { Heart, Database } from 'lucide-react';
import {
  FlavorDetails,
  FlavorRepository,
  MixDetails,
  MixRepository,
} from '../repositories/LocalDatabase';
import { mapDbMixToShishaMix } from '../repositories/mapper';
import { ShishaMix } from '../types';
import FlavorCard from './FlavorCard';
import FlavorDetailModal from './FlavorDetailModal';
import MixCard from './MixCard';
import MixDetailModal from './MixDetailModal';
import { showHUD } from './NotificationHUD';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';

export default function Favorites() {
  const [activeTab, setActiveTab] = useState<'mixes' | 'flavors'>('mixes');
  const [ownedFlavors, setOwnedFlavors] = useState<FlavorDetails[]>([]);
  const [favoriteMixes, setFavoriteMixes] = useState<MixDetails[]>([]);
  
  const [currentPageMixes, setCurrentPageMixes] = useState(1);
  const [pageWindowStartMixes, setPageWindowStartMixes] = useState(1);
  const [currentPageFlavors, setCurrentPageFlavors] = useState(1);
  const [pageWindowStartFlavors, setPageWindowStartFlavors] = useState(1);

  const [selectedFlavor, setSelectedFlavor] = useState<FlavorDetails | null>(
    null,
  );
  const [selectedMix, setSelectedMix] = useState<ShishaMix | null>(null);
  const [selectedDbMix, setSelectedDbMix] = useState<MixDetails | null>(null);

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedFlavorIds, setSelectedFlavorIds] = useState<string[]>([]);
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    const allFlavors = await FlavorRepository.getAll();
    const owned = allFlavors.filter((f) => f.owned);
    setOwnedFlavors(owned);

    const allMixes = await MixRepository.getAll();
    // is_favoriteフラグで絞り込み
    const favs = allMixes.filter((m) => m.is_favorite === true && m.user_id === 'local-user');
    setFavoriteMixes(favs);
  };

  const handleDeleteMix = async (mixId: string) => {
    try {
      await MixRepository.delete(mixId);
      showHUD({ type: 'success', message: 'ミックスを削除しました' });
      setSelectedMix(null);
      setSelectedDbMix(null);
      await loadFavorites();
    } catch (e) {
      showHUD({ type: 'error', message: '削除に失敗しました' });
    }
  };

  const handleFavorite = async (mixId: string) => {
    try {
      const newValue = await MixRepository.toggleFavorite(mixId);
      showHUD({
        type: 'success',
        message: newValue ? 'お気に入りに登録しました ♥' : 'お気に入りから外しました',
      });
      await loadFavorites();
      // 選択中のmixのisFavoriteを更新
      if (selectedDbMix && selectedDbMix.id === mixId) {
        setSelectedDbMix((prev) => prev ? { ...prev, is_favorite: newValue } : prev);
      }
      // お気に入りから外した場合はモーダルを閉じる
      if (!newValue) {
        setSelectedMix(null);
        setSelectedDbMix(null);
      }
    } catch (e) {
      showHUD({ type: 'error', message: 'お気に入りの更新に失敗しました' });
    }
  };

  const toggleFlavorSelection = (id: string) => {
    setSelectedFlavorIds((prev) => 
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  };

  const handleBulkUnownSelected = async () => {
    if (selectedFlavorIds.length === 0) return;
    setConfirmModalConfig({
      isOpen: true,
      title: '確認',
      message: `選択した${selectedFlavorIds.length}件のフレーバーを未所持にしますか？\n(※この操作は元に戻せません)`,
      onConfirm: async () => {
        try {
          await FlavorRepository.bulkSetUnowned(selectedFlavorIds);
          showHUD({ type: 'success', message: '一括で未所持に変更しました' });
          setSelectedFlavorIds([]);
          setIsSelectionMode(false);
          await loadFavorites();
        } catch (e) {
          showHUD({ type: 'error', message: '更新に失敗しました' });
        } finally {
          setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const ITEMS_PER_PAGE = 8;
  const totalMixPages = Math.ceil(favoriteMixes.length / ITEMS_PER_PAGE);
  const totalFlavorPages = Math.ceil(ownedFlavors.length / ITEMS_PER_PAGE);

  const paginatedMixes = favoriteMixes.slice(
    (currentPageMixes - 1) * ITEMS_PER_PAGE,
    currentPageMixes * ITEMS_PER_PAGE
  );

  const paginatedFlavors = ownedFlavors.slice(
    (currentPageFlavors - 1) * ITEMS_PER_PAGE,
    currentPageFlavors * ITEMS_PER_PAGE
  );

  return (
    <div
      style={{
        padding: '30px 20px',
        maxWidth: '1200px',
        margin: '0 auto',
        animation: 'fadeIn 0.5s ease-out',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          borderBottom: '1px solid rgba(231, 228, 223, 0.1)',
          paddingBottom: '16px',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-h1)',
              fontFamily: 'var(--font-heading-ja)',
              color: 'var(--color-text)',
            }}
          >
            お気に入り &amp; 所持リスト
          </h1>
          <p
            style={{
              margin: '4px 0 0 0',
              fontSize: 'var(--font-size-caption)',
              opacity: 0.7,
            }}
          >
            お気に入りのミックスと、現在手元にある所持フレーバーを管理
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '24px',
        }}
      >
        <button
          onClick={() => setActiveTab('mixes')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            borderRadius: '8px',
            backgroundColor:
              activeTab === 'mixes'
                ? 'var(--color-primary)'
                : 'var(--color-surface)',
            color: 'var(--color-text)',
            border: 'none',
            fontWeight: activeTab === 'mixes' ? 'bold' : 'normal',
            cursor: 'pointer',
          }}
        >
          <Heart size={16} />
          お気に入りミックス ({favoriteMixes.length})
        </button>

        <button
          onClick={() => setActiveTab('flavors')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            borderRadius: '8px',
            backgroundColor:
              activeTab === 'flavors'
                ? 'var(--color-primary)'
                : 'var(--color-surface)',
            color: 'var(--color-text)',
            border: 'none',
            fontWeight: activeTab === 'flavors' ? 'bold' : 'normal',
            cursor: 'pointer',
          }}
        >
          <Database size={16} />
          所持フレーバー ({ownedFlavors.length})
        </button>
      </div>

      {/* Grid Content */}
      {activeTab === 'mixes' ? (
        favoriteMixes.length === 0 ? (
          <div
            className="glass-panel"
            style={{
              padding: '40px',
              textAlign: 'center',
              opacity: 0.8,
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <p>お気に入り登録されたミックスレシピはありません。</p>
            <p style={{ fontSize: '13px', opacity: 0.6 }}>
              レシピ一覧から詳細カードを開き、♥ボタンで登録できます。
            </p>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '20px',
              }}
            >
              {paginatedMixes.map((dbMix) => {
                const mix = mapDbMixToShishaMix(dbMix);
                return (
                  <MixCard
                    key={mix.id}
                    mix={mix}
                    onClick={() => {
                      setSelectedMix(mix);
                      setSelectedDbMix(dbMix);
                    }}
                  />
                );
              })}
            </div>
            <Pagination
              currentPage={currentPageMixes}
              totalPages={totalMixPages}
              onPageChange={setCurrentPageMixes}
              pageWindowStart={pageWindowStartMixes}
              onPageWindowStartChange={setPageWindowStartMixes}
            />
          </>
        )
      ) : ownedFlavors.length === 0 ? (
        <div
          className="glass-panel"
          style={{
            padding: '40px',
            textAlign: 'center',
            opacity: 0.8,
            backgroundColor: 'var(--color-surface)',
          }}
        >
          <p>所持しているフレーバーはありません。</p>
          <p style={{ fontSize: '13px', opacity: 0.6 }}>
            フレーバーライブラリから所持フラグをONに設定してください。
          </p>
        </div>
      ) : (
        <>
          {/* Flavors Action Bar */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', gap: '12px' }}>
            <button
              onClick={() => {
                setIsSelectionMode(!isSelectionMode);
                setSelectedFlavorIds([]);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                backgroundColor: isSelectionMode ? 'var(--color-accent)' : 'var(--color-surface)',
                color: isSelectionMode ? 'var(--color-background)' : 'var(--color-text)',
                border: isSelectionMode ? '1px solid var(--color-accent)' : '1px solid rgba(231, 228, 223, 0.1)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: isSelectionMode ? 'bold' : 'normal',
                transition: 'all 0.2s',
              }}
            >
              選択モード {isSelectionMode ? 'ON' : 'OFF'}
            </button>

            {isSelectionMode && selectedFlavorIds.length > 0 && (
              <button
                onClick={handleBulkUnownSelected}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 100, 100, 0.1)',
                  color: '#ff6b6b',
                  fontWeight: 'bold',
                  border: '1px solid rgba(255, 100, 100, 0.3)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  transition: 'all 0.2s',
                }}
              >
                選択した{selectedFlavorIds.length}件を未所持に
              </button>
            )}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '20px',
            }}
          >
            {paginatedFlavors.map((flavor) => (
              <FlavorCard
                key={flavor.id}
                flavor={flavor}
                onClick={() => {
                  if (isSelectionMode) {
                    toggleFlavorSelection(flavor.id);
                  } else {
                    setSelectedFlavor(flavor);
                  }
                }}
                selectable={isSelectionMode}
                selected={selectedFlavorIds.includes(flavor.id)}
                onSelect={() => toggleFlavorSelection(flavor.id)}
              />
            ))}
          </div>
          <Pagination
            currentPage={currentPageFlavors}
            totalPages={totalFlavorPages}
            onPageChange={setCurrentPageFlavors}
            pageWindowStart={pageWindowStartFlavors}
            onPageWindowStartChange={setPageWindowStartFlavors}
          />
        </>
      )}

      {selectedFlavor && (
        <FlavorDetailModal
          flavor={selectedFlavor}
          onClose={() => setSelectedFlavor(null)}
          onSaveSuccess={loadFavorites}
        />
      )}

      {selectedMix && (
        <MixDetailModal
          mix={selectedMix}
          onClose={() => {
            setSelectedMix(null);
            setSelectedDbMix(null);
          }}
          onLike={() => {}}
          onBookmark={() => {}}
          onAddComment={() => {}}
          isLiked={false}
          isBookmarked={false}
          onFavorite={handleFavorite}
          isFavorite={selectedDbMix?.is_favorite ?? true}
        />
      )}

      <ConfirmModal
        isOpen={confirmModalConfig.isOpen}
        title={confirmModalConfig.title}
        message={confirmModalConfig.message}
        onConfirm={confirmModalConfig.onConfirm}
        onCancel={() => setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }))}
        isDestructive={true}
      />
    </div>
  );
}
